/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, lodash, propsUtils } from '@ba-ui-toolkit/utils';
import { FlexItem } from '@ba-ui-toolkit/flexlayout';
import Slideout from '@ba-ui-toolkit/slideout';
import Container from '@ba-ui-toolkit/container';
import { omit } from 'lodash';
import TabList from './TabList';
import TabPanel from './TabPanel';
import './Tabs.scss';
import stringResource from './nls/StringResource';

const { BaPropTypes } = propsUtils;

const { uniqueId, findIndex, isEqual } = lodash;

function getTabIndex(componentChildren, tabProps) {
	const children = Children.toArray(componentChildren);
	const index = findIndex(children, ({ props }) => props.id === tabProps.selected);

	return index >= 0 ? index : -1;
}
export default class Tabs extends Component {
	static propTypes = {
		draggable: PropTypes.bool,
		selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		children: PropTypes.node.isRequired,
		onChange: PropTypes.func,
		onAddButtonClick: PropTypes.func,
		addable: PropTypes.bool,
		scrollWhenSelected: PropTypes.bool,
		scrollWhenAdded: PropTypes.bool,
		onReorder: PropTypes.func,
		setState: PropTypes.func,
		onDeletePrompt: PropTypes.func,
		deleteable: PropTypes.bool,
		multiLine: PropTypes.bool,
		editable: PropTypes.bool,
		pageLevel: BaPropTypes.deprecated(
			'It will be removed soon, use "editable" and "deleteable" properties instead.'
		),
		vertical: PropTypes.bool,
		placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
		tabButton: PropTypes.bool,
		overflow: PropTypes.string,
		className: PropTypes.string,
		linePosition: PropTypes.string,
		addTabToolTipText: PropTypes.string,
		newTabContent: PropTypes.object,
		stateTabs: PropTypes.array,
		slideout: PropTypes.bool,
		pinAll: PropTypes.bool,
		toolbar: PropTypes.array,
		align: PropTypes.oneOf(['start', 'end', 'left', 'center', 'right']),
		style: PropTypes.object,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		scrollButtonsBackgroundColor: PropTypes.string,
		scrollButtonsTextColor: PropTypes.string,
		scrollButtonsHoverColor: PropTypes.string,
	};

	static defaultProps = {
		scrollWhenSelected: true,
		scrollWhenAdded: false,
		align: 'start',
		pinAll: false,
		className: '',
		style: {},
	};

	state = {
		selectedIndex: getTabIndex(this.props.children, this.props),
		selectedChanged: false,
		newTabAdded: false,
	};

	componentWillReceiveProps(props) {
		const i = getTabIndex(props.children, props);
		if (i >= 0 && i !== this.state.selectedIndex) {
			this.setState({ selectedIndex: i, selectedChanged: true });
		}
		if (i < 0) {
			this.setState({ selectedIndex: -1 });
		}

		if (!isEqual(props.newTabContent, this.props.newTabContent)) {
			// this.addNewTab(props);
		}
	}

	componentDidUpdate() {
		if (this.state.newTabAdded) {
			if (this.props.scrollWhenAdded) {
				this.refs.tablist.scrollTabIntoView(
					this.refs.tablist._innerNode.firstChild.lastChild
				);
			}
			this.setState({ newTabAdded: false });
		}

		if (this.state.selectedChanged) {
			if (this.props.scrollWhenSelected) {
				this.refs.tablist.scrollTabIntoView();
			}
			this.setState({ selectedChanged: false });
		}
	}

	componentDidMount() {
		if (this.props.scrollWhenSelected && this.refs.tablist) {
			this.refs.tablist.scrollTabIntoView();
		}
	}

	_id = uniqueId('__tabs_');

	_renderedTabs = {};

	_onChange = (value, id) => {
		if (this.props.selected === undefined) {
			// internally controlled
			this.setState({ selectedIndex: id, selectedChanged: true });
		}

		// this part controls the toggle for tabbed slideout
		if (this.state.selectedIndex === id && this.props.slideout) {
			this.setState({ selectedIndex: -1 });
			// this.props.onChange(value, id);
		} else {
			this.props.onChange && this.props.onChange(value, id);
		}
	};

	deleteHandler = (value, i) => {
		const currentTab = this.state.selectedIndex;
		const obj = this.props.stateTabs;
		const arr = [];
		Object.keys(obj).forEach(v => {
			if (obj[v].props.id !== value) {
				arr.push(obj[v]);
			}
		});
		this.props.setState(arr);

		if (arr.length === 0) {
			/* If there are no tabs, select none */
			this.setState({
				selectedIndex: null,
			});
		} else if (currentTab === i) {
			/* If the tab you delete is the selected tab, select the one before it, or the first tab */
			const newId = Math.max(i - 1, 0);
			this.setState({
				selectedIndex: newId,
				selectedChanged: true,
			});

			this.props.onChange && this.props.onChange(arr[newId].props.id, newId);
		} else if (currentTab > this.props.children.length - 1 || currentTab > i) {
			/* If the selected index is the last tab, move left one OR the deleted one is before the selected one */
			const newId = Math.max(currentTab - 1, 0);

			this.setState({
				selectedIndex: newId,
				selectedChanged: true,
			});
			this.props.onChange && this.props.onChange(arr[newId].props.id, newId);
		}
	};

	addNewTab = () => {
		const stateTabsObject = this.props.stateTabs;
		const id = uniqueId();
		stateTabsObject.push(
			<TabPanel id={id} key={id} label={`Untitled ${id}`} subTitle="Subtitle">
				{`${stringResource.get('newContent')} ${id}`}
			</TabPanel>
		);
		this.setState({ newTabAdded: true });
		this.props.setState(stateTabsObject);
	};

	moveTab = (dragIndex, hoverIndex, dropped, tabId) => {
		const stateTabsObject = this.props.stateTabs;
		const dragTab = stateTabsObject[dragIndex];
		this.props.onChange && this.props.onChange(dragTab.props.id);

		this.arrayMove(stateTabsObject, dragIndex, hoverIndex);

		this.props.setState(stateTabsObject);
		this.setState({ selectedIndex: hoverIndex }, () => {
			dropped && this.props.onReorder && this.props.onReorder(hoverIndex, tabId);
		});
	};

	/**
	 * Increments a tabs position in the list.
	 *
	 * @param fromIndex {number}
	 * @param direction {number} - direction to move the tab, 1 for right and -1 for left.
	 */
	incrementTab = (fromIndex, direction, tabId) => {
		const tabs = this.props.stateTabs;
		if (!tabs) {
			return;
		}

		let toIndex = fromIndex + direction;
		// wrap position
		if (toIndex < 0) {
			toIndex = tabs.length - 1;
		}
		if (toIndex > tabs.length - 1) {
			toIndex = 0;
		}

		this.moveTab(fromIndex, toIndex, true, tabId);
	};

	onEditLabel = (value, newLabel) => {
		const stateTabsObject = this.props.stateTabs;
		let newEl;
		Children.toArray(stateTabsObject).forEach((item, index) => {
			if (item.props.id === value) {
				newEl = cloneElement(item, {
					label: newLabel,
				});
				stateTabsObject[index] = newEl;
			}
		});
		this.forceUpdate();
	};

	arrayMove(arr, fromIndex, toIndex) {
		const element = arr[fromIndex];
		arr.splice(fromIndex, 1);
		arr.splice(toIndex, 0, element);
	}

	setClickedTrue = () => {
		this.setState({ clicked: true });
	};

	setClickedFalse = () => {
		this.setState({ clicked: false });
	};

	render() {
		const {
			overflow,
			align,
			multiLine,
			vertical,
			slideout,
			pinAll,
			draggable,
			className,
			style,
			pageLevel,
			tabButton,
			addTabToolTipText,
			textDir,
			scrollButtonsBackgroundColor,
			scrollButtonsTextColor,
			scrollButtonsHoverColor,
			onDeletePrompt,
			placement,
		} = this.props;

		let { deleteable, editable, addable } = this.props;

		if (pageLevel) {
			deleteable = true;
			editable = true;
			addable = true;
		}

		const { selectedIndex } = this.state;

		let linePositionClass;
		let tabsPlacementClass;
		let verticalPlacement;

		if (placement) {
			tabsPlacementClass = placement ? `tabs-placement-${placement}` : false;
			verticalPlacement = placement === 'left' || placement === 'right';
		} else {
			verticalPlacement = vertical;
		}

		if (!tabButton) {
			linePositionClass = 'line-position-bottom';
			if (verticalPlacement) {
				linePositionClass = 'line-position-left';
			}
			if (this.props.linePosition) {
				linePositionClass = `line-position-${this.props.linePosition}`;
			}
		} else if (tabButton) {
			linePositionClass = 'line-position-none';
		}

		const classNames = cx(
			'ba-common-tabList__container',
			linePositionClass,
			tabsPlacementClass,
			{
				deleteable,
				multiLine,
				vertical: verticalPlacement,
				slideout,
				clicked: this.state.clicked,
				[`align_${align}`]: align === 'center' || align === 'end',
			}
		);

		const children = Children.toArray(this.props.children).filter(child => child !== null);
		const renderedTabs = {};
		const result = (
			<div
				direction="column"
				className={`${classNames} ${className}`}
				style={style}
				onMouseDown={this.setClickedTrue}
				onKeyUp={this.setClickedFalse}
			>
				<FlexItem
					gutter={[1, 0, 0]}
					style={{ position: 'relative' }}
					ref={node => {
						slideout && !this.state.node && this.setState({ node });
					}}
				>
					<TabList
						ref="tablist"
						scrollButtonsBackgroundColor={scrollButtonsBackgroundColor}
						scrollButtonsTextColor={scrollButtonsTextColor}
						scrollButtonsHoverColor={scrollButtonsHoverColor}
						draggable={draggable}
						selectedIndex={selectedIndex}
						deleteable={deleteable}
						multiLine={multiLine}
						editable={editable}
						vertical={verticalPlacement}
						tabButton={tabButton}
						addable={addable}
						textDir={textDir}
						deleteHandler={this.deleteHandler}
						onDeletePrompt={onDeletePrompt}
						onChange={this._onChange}
						onEditLabel={this.onEditLabel}
						linePositionClass={linePositionClass}
						addTabToolTipText={addTabToolTipText}
						onAddButtonClick={() => {
							(this.props.onAddButtonClick || this.addNewTab)(); // this.props.onAddButtonClick
						}}
						moveTab={this.moveTab}
						incrementTab={this.incrementTab}
						options={Children.map(children, ({ props }) => ({
							...omit(
								props,
								'children',
								'id',
								'pinnable',
								'resizable',
								'resizeMaxConstraintsX',
								'resizeMinConstraintsX'
							),
							panelId: `${this._id}-${props.id}_panel`,
							value: props.id,
							editable: !slideout && props.editable,
							toolbar: this.props.toolbar,
						}))}
					/>
					{slideout &&
						Children.map(children, (child, i) => {
							const { props } = child;
							const selected = selectedIndex === i;
							// track if tab is previously rendered
							const renderTab =
								!props.disabled &&
								((props.keepTabContent && this._renderedTabs[props.id]) ||
									selected);
							renderedTabs[props.id] = renderTab;
							const tabId = `${this._id}-${props.id}`;

							const slideoutProps = {
								active: selected,
								resizable: child.props.resizable,
								resizeMaxConstraintsX: child.props.resizeMaxConstraintsX,
								resizeMinConstraintsX: child.props.resizeMinConstraintsX,
								resizeMaxConstraintsY: child.props.resizeMaxConstraintsY,
								resizeMinConstraintsY: child.props.resizeMinConstraintsY,
								containerRef: this.state.node,
								width: child.props.width,
							};
							if (!pinAll && !child.props.pinnable) {
								slideoutProps.onClickAway = () => {
									if (selected) {
										this.setState({ selectedIndex: -1 });
									}
								};
							}

							return (
								<Slideout {...slideoutProps}>
									<div
										key={props.id}
										id={`${tabId}_panel`}
										role="tabpanel"
										className="tabpanel"
										aria-labelledby={tabId}
										aria-hidden={!selected}
										overflow={overflow}
									>
										{/* {renderTab ? React.Children.map(props.children, (innerChild, x) => innerChild) : null} */}
										{React.Children.map(
											props.children,
											innerChild => innerChild
										)}
									</div>
								</Slideout>
							);
						})}
				</FlexItem>
				{!slideout && (
					<Container
						relative
						overflow={overflow}
						className="ba-common-tabList__innerContainer"
					>
						{Children.map(children, (child, i) => {
							const { props } = child;
							const selected = selectedIndex === i;
							// track if tab is previously rendered
							const renderTab =
								!props.disabled &&
								((props.keepTabContent && this._renderedTabs[props.id]) ||
									selected);
							renderedTabs[props.id] = renderTab;
							const tabId = `${this._id}-${props.id}`;

							return (
								<div
									key={props.id}
									id={`${tabId}_panel`}
									role="tabpanel"
									className="tabpanel"
									aria-labelledby={tabId}
									style={{ display: selected ? 'block' : 'none' }}
									aria-hidden={!selected}
									overflow={overflow}
								>
									{renderTab
										? React.Children.map(
												props.children,
												innerChild => innerChild
										  )
										: null}
								</div>
							);
						})}
					</Container>
				)}
			</div>
		);
		this._renderedTabs = renderedTabs;

		return result;
	}
}
